import isEqual from "lodash-es/isEqual";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";

import BeforeAfterCommands from "components/BeforeAfterCommands";
import { Commands } from "components/BeforeAfterCommands/types";
import StackHooks from "components/DocumentationSnippets/StackHooks";
import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import { useObserveForWarning } from "components/WarningContext/useObserveForWarning";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import Counter from "ds/components/Counter";
import FormField from "ds/components/Form/Field";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalBodyHeader from "ds/components/FullScreenModal/BodyHeader";
import ListBoxItem from "ds/components/ListBox/Item";
import Select from "ds/components/Select";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import Typography from "ds/components/Typography";
import { useFormValidations } from "hooks/useFormValidations";
import useTypedContext from "hooks/useTypedContext";
import { getDocsUrl } from "utils/getDocsUrl";

import NewStackFooter from "../Footer";
import { StackFormContext } from "../context";
import {
  adaptCommandsToFields,
  adaptFieldsToCommands,
  adaptFieldsToCompare,
} from "../hooksAdapters";
import { HookCommands, HooksPhases, StackCreationWizardStep, StackHooksFormFields } from "../types";
import { useNewStackAnalyticsSegmentEvent } from "../useNewStackAnalyticsSegmentEvent";
import useStackCreationAnalyticsVersion from "../useStackCreationAnalyticsVersion";
import { getTooltipAnalyticsProps } from "../utils";
import { useValidation } from "./useValidation";

const NewStackAddHooks = () => {
  const [selectedPhase, setSelectedPhase] = useState<HooksPhases>(HooksPhases.Init);
  const { currentStep, updateStepData, formData } = useTypedContext(StackFormContext);
  const analyticsVersion = useStackCreationAnalyticsVersion();
  const trackSegmentEvent = useNewStackAnalyticsSegmentEvent();
  const stepData = formData[StackCreationWizardStep.Hooks];
  const [hasUnsubmittedChanges, setHasUnsubmittedChanges] = useState(false);

  const [beforeValue, setBeforeValue] = useState("");
  const [afterValue, setAfterValue] = useState("");

  const onBeforeInputChange = (value: string) => {
    setBeforeValue(value);
    setHasUnsubmittedChanges?.(!!(value || afterValue));
  };

  const onAfterInputChange = (value: string) => {
    setAfterValue(value);
    setHasUnsubmittedChanges?.(!!(value || beforeValue));
  };

  useObserveForWarning(
    hasUnsubmittedChanges,
    <>
      You have not added the <strong>command</strong>. Do you want to continue without adding it?
    </>
  );

  // needed for save & continue button to detect changes, because data is stored in formData not in local state
  const initialStepData = useRef(stepData);

  const { bannerError, isWorkflowValidForChosenVendor, isStepOptional } = useValidation();

  const [commandsByPhase, setCommandsByPhase] = useState<Record<HooksPhases, HookCommands>>(
    adaptFieldsToCommands(stepData)
  );

  const builderForm = useForm<StackHooksFormFields>({
    defaultValues: {
      ...stepData,
    },
    mode: "onChange",
  });
  const runFormValidations = useFormValidations(builderForm);

  const processStepData = () => {
    return formData;
  };

  const isDataChanged = !isEqual(
    adaptFieldsToCompare(initialStepData.current),
    adaptFieldsToCompare(stepData)
  );

  const handleCommandsChange = (commands: Commands) => {
    setCommandsByPhase({ ...commandsByPhase, [selectedPhase]: commands });
    updateStepData(currentStep, {
      ...stepData,
      ...adaptCommandsToFields(commands as HookCommands, selectedPhase),
    });

    trackSegmentEvent("Hooks modified", {
      phase: selectedPhase,
      before: commands.before?.length,
      after: commands.after?.length,
    });
  };

  const phasesOptions = Object.values(HooksPhases).map((phase) => ({
    label: phase,
    value: phase,
  }));

  const currentPhaseCommands = commandsByPhase[selectedPhase];

  return (
    <>
      <FullScreenModalBody>
        <FullScreenModalBodyHeader
          title={<>Add hooks {isStepOptional && "(optional)"}</>}
          description="Spacelift workflow can be customized by adding extra commands to be executed before and
            after each of the phases"
        />

        <Box direction="column" gap="medium">
          {bannerError && <Banner variant="danger">{bannerError}</Banner>}

          <FormField
            label="Customize workflow"
            tooltipInfoVariant="modal"
            {...getTooltipAnalyticsProps("Add hooks", "Customize workflow", {
              version: analyticsVersion,
            })}
            tooltipInfo={
              <>
                <TooltipModalTitle>Customize workflow</TooltipModalTitle>
                <TooltipModalBody align="start">
                  <Typography tag="p" variant="p-body3">
                    Customize Spacelift run lifecycle by adding commands to run before and after
                    various execution phases.
                  </Typography>
                  {/** TODO: update docs */}
                  <ReadMoreDocsLink
                    docsUrl={getDocsUrl("/concepts/stack/stack-settings#customizing-workflow")}
                  />
                </TooltipModalBody>
              </>
            }
          >
            {({ ariaInputProps }) => (
              <Select
                value={selectedPhase}
                items={phasesOptions}
                onChange={(value) => setSelectedPhase(value as HooksPhases)}
                {...ariaInputProps}
              >
                {({ label, value }) => {
                  const commands = commandsByPhase[value];
                  const beforeCommandsCount = commands.before?.length ?? 0;
                  const afterCommandsCount = commands.after?.length ?? 0;
                  const totalCount = beforeCommandsCount + afterCommandsCount;
                  const counterVariant = isWorkflowValidForChosenVendor(value)
                    ? "default"
                    : "danger";

                  return (
                    <ListBoxItem
                      id={value}
                      key={label}
                      textValue={label}
                      isSelected={selectedPhase === value}
                    >
                      <Box justify="between" align="center" grow="1">
                        <Typography tag="span" variant="p-body2">
                          {label}
                        </Typography>
                        <Counter variant={counterVariant} count={totalCount} />
                      </Box>
                    </ListBoxItem>
                  );
                }}
              </Select>
            )}
          </FormField>
          <BeforeAfterCommands
            onAfterInputChange={onAfterInputChange}
            onBeforeInputChange={onBeforeInputChange}
            commands={currentPhaseCommands}
            onChange={handleCommandsChange}
            // TODO update stack on add
          />
        </Box>
      </FullScreenModalBody>
      <NewStackFooter
        isDataChanged={isDataChanged}
        processStepData={processStepData}
        documentationLink={getDocsUrl("/concepts/stack/stack-settings#customizing-workflow")}
        documentationTitle="Add hooks"
        documentationBody={<StackHooks />}
        runFormValidations={runFormValidations}
      />
    </>
  );
};

export default NewStackAddHooks;
